import { useCallback, useMemo, useState } from 'react';

// BO 模式 -> 局数
const MODE_TO_GAMES = { BO1: 1, BO3: 3, BO5: 5 };

// 教练 BO 状态机
// - mode: 'BO1' / 'BO3' / 'BO5'
// - fearlessEnabled: 是否启用全局 Pick 锁定
// - currentGameIndex: 当前局 0-based 序号
// - currentFirstSide: 当前局谁先手（'blue' / 'red'）
// - nextFirstSide: 用户预设的下一局先手（点 "进入下一局" 时生效）
// - completedGames: 已完成局的快照数组（含 picks/bans/firstSide）
const useCoachBO = () => {
  const [mode, setMode] = useState('BO3');
  const [fearlessEnabled, setFearlessEnabled] = useState(true);
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [currentFirstSide, setCurrentFirstSide] = useState('blue');
  const [nextFirstSide, setNextFirstSide] = useState('red'); // 默认下局换边
  const [completedGames, setCompletedGames] = useState([]);

  // 当前正在"查看"的局 index：null = 看当前正在打的局；数字 = 复盘某历史局（只读）
  const [viewingGameIndex, setViewingGameIndex] = useState(null);

  const totalGames = MODE_TO_GAMES[mode] || 1;
  const isSeriesComplete = currentGameIndex >= totalGames;
  const isReplayMode = viewingGameIndex !== null && viewingGameIndex !== currentGameIndex;
  // 复盘时拿到的历史快照（含 bluePicks/redPicks/blueBans/redBans/firstSide）
  const replaySnapshot = isReplayMode
    ? (completedGames.find((g) => g.gameIndex === viewingGameIndex) || null)
    : null;

  // 全局 Pick 锁定表：championId -> { gameIndex, side, championName }
  // 只跟踪 picks（用户明确要求 ban 不锁），且只在 fearlessEnabled = true 时生效
  const globalLockedMap = useMemo(() => {
    const map = new Map();
    if (!fearlessEnabled) return map;
    completedGames.forEach((game) => {
      const writeSide = (sidePicks, side) => {
        sidePicks.forEach((champ) => {
          if (!champ || !champ.id) return;
          if (!map.has(champ.id)) {
            map.set(champ.id, {
              gameIndex: game.gameIndex,
              gameNumber: game.gameIndex + 1,
              side,
              championName: champ.name,
            });
          }
        });
      };
      writeSide(game.bluePicks, 'blue');
      writeSide(game.redPicks, 'red');
    });
    return map;
  }, [completedGames, fearlessEnabled]);

  // 由调用方在 draft.isFinished 后调用此函数完成进局
  // 参数 snapshot = { bluePicks, redPicks, blueBans, redBans }
  const recordGameAndAdvance = useCallback(
    (snapshot) => {
      setCompletedGames((prev) => [
        ...prev,
        {
          gameIndex: currentGameIndex,
          firstSide: currentFirstSide,
          ...snapshot,
        },
      ]);
      setCurrentGameIndex((n) => n + 1);
      setCurrentFirstSide(nextFirstSide);
      // 下一局的"下下一局"先手默认再换一次（继续轮换）
      setNextFirstSide((s) => (s === 'blue' ? 'red' : 'blue'));
      // 进入新一局时跳回"当前局"视图
      setViewingGameIndex(null);
    },
    [currentGameIndex, currentFirstSide, nextFirstSide],
  );

  // 在复盘视图编辑后写回某历史局 —— 自动触发 Fearless 全局锁重算
  // patch 形状（可只传部分字段）：
  //   { bluePicks?, redPicks?, blueBans?, redBans? }
  //   每个字段是 champion 对象数组（length 5，空位填 null 即可，或缩短数组）
  const updateGameSnapshot = useCallback((gameIndex, patch) => {
    if (typeof gameIndex !== 'number') return;
    setCompletedGames((prev) => prev.map((g) => {
      if (g.gameIndex !== gameIndex) return g;
      const merged = { ...g };
      if (patch.bluePicks) merged.bluePicks = patch.bluePicks.slice(0, 5);
      if (patch.redPicks)  merged.redPicks  = patch.redPicks.slice(0, 5);
      if (patch.blueBans)  merged.blueBans  = patch.blueBans.slice(0, 5);
      if (patch.redBans)   merged.redBans   = patch.redBans.slice(0, 5);
      return merged;
    }));
  }, []);

  // 切换查看：传 null 或当前局 index → 回到正在打的局；传历史 index → 只读复盘
  const setViewingGame = useCallback((index) => {
    if (index == null) {
      setViewingGameIndex(null);
      return;
    }
    setViewingGameIndex(index);
  }, []);

  const returnToCurrent = useCallback(() => {
    setViewingGameIndex(null);
  }, []);

  // 真撤销：把 BO 状态回退到指定局开始之前（破坏性操作！UI 调用前要弹确认）
  // - 截断 completedGames 至 [0, targetIndex)
  // - currentGameIndex = targetIndex
  // - currentFirstSide 用那一局原始的 firstSide（保持记录一致）
  // - 调用方负责重置 useCoachDraft / timer
  const rollbackToGame = useCallback((targetIndex) => {
    if (targetIndex < 0 || targetIndex >= totalGames) return;
    setCompletedGames((prev) => {
      const next = prev.filter((g) => g.gameIndex < targetIndex);
      // 回放原 firstSide（如果有），否则保持现状
      const origFirst = prev.find((g) => g.gameIndex === targetIndex);
      if (origFirst && origFirst.firstSide) {
        setCurrentFirstSide(origFirst.firstSide);
        setNextFirstSide(origFirst.firstSide === 'blue' ? 'red' : 'blue');
      }
      return next;
    });
    setCurrentGameIndex(targetIndex);
    setViewingGameIndex(null);
  }, [totalGames]);

  // 切换 BO 模式：重置整个 series（避免半路减少局数导致非法状态）
  const changeMode = useCallback((newMode) => {
    if (!MODE_TO_GAMES[newMode] || newMode === mode) return;
    setMode(newMode);
    setCurrentGameIndex(0);
    setCompletedGames([]);
    setCurrentFirstSide('blue');
    setNextFirstSide('red');
    setViewingGameIndex(null);
  }, [mode]);

  const toggleFearless = useCallback(() => {
    setFearlessEnabled((v) => !v);
  }, []);

  // 当前局未开始时可切换"当前局先手"，开始后通过 nextFirstSide 提前预约下一局
  const toggleCurrentFirstSide = useCallback(() => {
    setCurrentFirstSide((s) => (s === 'blue' ? 'red' : 'blue'));
  }, []);

  const toggleNextFirstSide = useCallback(() => {
    setNextFirstSide((s) => (s === 'blue' ? 'red' : 'blue'));
  }, []);

  const resetBO = useCallback(() => {
    setCurrentGameIndex(0);
    setCompletedGames([]);
    setCurrentFirstSide('blue');
    setNextFirstSide('red');
    setViewingGameIndex(null);
  }, []);

  // ========== 存档：序列化 & 反序列化 ==========

  // 输出当前 BO 状态的"游戏部分"，给 useBOArchive.makeArchive 用
  // 注意：胜负/队名/label 由调用方（CoachApp）拼装，这里不掺和
  const serializeCurrent = useCallback(() => {
    const firstSide = (completedGames[0] && completedGames[0].firstSide) || currentFirstSide;
    return {
      mode,
      totalGames,
      firstSide,
      fearlessEnabled,
      games: completedGames,            // 直接吃当前 completedGames（含完整 champion 对象）
    };
  }, [mode, totalGames, fearlessEnabled, completedGames, currentFirstSide]);

  // 从存档恢复整个 BO 状态（破坏性：会覆盖当前正在打的 BO）
  // - archive：useBOArchive 的存档对象（含 slim champion）
  // - resolveChampion：可选 (slim) => fullChampion，把 slim 还原成完整对象（含立绘 URL 等）
  //                    不传则用 slim 本身（导出/复盘仍可显示，但立绘可能为空）
  const loadFromArchive = useCallback((archive, resolveChampion) => {
    if (!archive || !Array.isArray(archive.games)) return;
    const resolver = typeof resolveChampion === 'function' ? resolveChampion : (s) => s;
    const resolveList = (list) => (list || []).map(resolver).filter(Boolean);
    const restoredGames = archive.games.map((g) => ({
      gameIndex: g.gameIndex,
      firstSide: g.firstSide || 'blue',
      bluePicks: resolveList(g.bluePicks),
      redPicks:  resolveList(g.redPicks),
      blueBans:  resolveList(g.blueBans),
      redBans:   resolveList(g.redBans),
    }));
    setMode(archive.mode || 'BO3');
    setFearlessEnabled(Boolean(archive.fearlessEnabled));
    setCompletedGames(restoredGames);
    setCurrentGameIndex(restoredGames.length);  // 跳到"下一局"位置（如果全打完就是 series complete）
    // 当前局先手：用存档里的最后一局之后的预期
    const lastFirst = restoredGames.length > 0
      ? restoredGames[restoredGames.length - 1].firstSide
      : (archive.firstSide || 'blue');
    setCurrentFirstSide(lastFirst === 'blue' ? 'red' : 'blue'); // 默认轮换
    setNextFirstSide(lastFirst === 'blue' ? 'blue' : 'red');
    setViewingGameIndex(null);
  }, []);

  return {
    mode,
    totalGames,
    fearlessEnabled,
    currentGameIndex,        // 0-based
    currentGameNumber: currentGameIndex + 1,
    currentFirstSide,        // 'blue' | 'red'
    nextFirstSide,           // 'blue' | 'red'
    completedGames,
    globalLockedMap,
    isSeriesComplete,

    // 历史复盘
    viewingGameIndex,        // null = 看当前局；数字 = 复盘历史局
    isReplayMode,            // 派生：是否处于只读复盘
    replaySnapshot,          // 复盘时的历史局快照（含 picks/bans/firstSide）
    setViewingGame,
    returnToCurrent,
    rollbackToGame,          // 破坏性撤销（UI 调用前应弹确认）
    updateGameSnapshot,      // 复盘编辑后写回 + 自动重算 Fearless

    changeMode,
    toggleFearless,
    toggleCurrentFirstSide,
    toggleNextFirstSide,
    recordGameAndAdvance,
    resetBO,

    // 存档
    serializeCurrent,        // () => { mode, totalGames, firstSide, fearlessEnabled, games }
    loadFromArchive,         // (archive, resolveChampion?) → 覆盖当前 BO
  };
};

export default useCoachBO;
